import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import Lister from "../../../../components/Lister/Lister";
import { useSession } from "../../../../contexts/SessionContext";
import { DelayedCohort } from "../../../../models/DelayedCohort";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import { fetchDelayedCohorts } from "./DelayedCohorts.function";

const DelayedCohorts = () => {
  const { language } = useSession();
  const navigate = useNavigate();

  const [cohorts, setCohorts] = useState<DelayedCohort[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchDelayedCohorts(setCohorts, setLoading, language);
  }, [language]);

  const items = useMemo(
    () =>
      cohorts.map((c) => ({
        id: c.cohort_id,
        name: c.cohort_name,
        leader: c.cohort_leader_name,
        program_manager: c.program_manager_name || "N/A",
        end_date: c.dt_end,
        sprint: c.active_sprint_name,
        status: c.status,
        size: c.cohort_size,
        delayed: c.delayed_students_count,
      })),
    [cohorts]
  );

  if (!loading && cohorts.length === 0) {
    return null;
  }

  const columns = [
    { key: "id", header: translate(language, "GLOBAL.ID") },
    { key: "name", header: translate(language, "GLOBAL.NAME") },
    { key: "leader", header: translate(language, "PAGES.HOME.DELAYED_COHORTS.COLUMN.LEADER") },
    {
      key: "program_manager",
      header: translate(language, "GLOBAL.PROGRAM_MANAGER"),
    },
    {
      key: "end_date",
      header: translate(language, "GLOBAL.END_DATE"),
      sortType: "date",
      render: (item: any) => (
        <span title={formatDateHumanly(language, item.end_date, true)}>
          {formatDateHumanly(language, item.end_date)}
        </span>
      ),
    },
    {
      key: "sprint",
      header: translate(language, "PAGES.HOME.DELAYED_COHORTS.COLUMN.CURRENT_SPRINT"),
    },

    {
      key: "status",
      header: translate(language, "GLOBAL.STATUS"),
      render: (item: any) => (
        <span
          className={`badge ${
            item.status === "urgent" ? "bg-danger" : item.status === "late" ? "text-dark bg-warning" : "bg-success"
          }`}
        >
          {translate(language, `PAGES.HOME.DELAYED_COHORTS.STATUS.${item.status.toUpperCase()}`)}
        </span>
      ),
    },

    {
      key: "size",
      header: translate(language, "PAGES.HOME.DELAYED_COHORTS.COLUMN.SIZE"),
      render: (item: any) => (
        <div>
          {item.size} <span className="ms-1 dst-icon-users-01" />
        </div>
      ),
    },

    {
      key: "delayed",
      header: translate(language, "PAGES.HOME.DELAYED_COHORTS.COLUMN.STUDENTS_LATE"),
      render: (item: any) => (
        <div className="text-danger">
          {item.delayed} <span className="ms-1 dst-icon-users-01" />
        </div>
      ),
    },
  ];

  return (
    <Card className="shadow-sm mx-auto m-regular mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="fw-bold s-regular">{translate(language, "PAGES.HOME.DELAYED_COHORTS.TITLE")}</div>
          <Link to="/cohorts/delayed" className="xs-regular">
            {translate(language, "PAGES.HOME.DELAYED_COHORTS.VIEW_ALL")}
          </Link>
        </div>
        <Lister
          columns={columns}
          items={items}
          mock={loading}
          additionalButton={(item: any) => (
            <span
              className="clickable xs-regular"
              onClick={(event) => completeNavigate(event, navigate, `/cohort/${item.id}`)}
            >
              <span className="dst-icon-arrow-narrow-right s-regular" />
            </span>
          )}
        />
      </Card.Body>
    </Card>
  );
};

export default DelayedCohorts;
